import { detectIE } from "../../../../Engine/utils";
import { isNullOrEmpty } from "../../../../../../Common/TypingUtils";

import {
    addressLevels,
    divisionType,
    FiasAddressFields,
    FiasCountry,
    FiasFlatRoom,
    FiasRegion,
    FiasStead,
} from "./Types/FiasTypes";
import { acceptHelperConvertationToValue } from "./Utils/Convertations/acceptConvertationHelper";
import { fiasConvertationMethods } from "./Utils/Convertations/Types";

export const maxItems = 6;
export const maxSteads = 3;

export type RegionsType = Array<{ region: FiasRegion }>;

export interface FiasClientInitOptions {
    division?: divisionType;
    maxItems?: number;
    addressLevel?: addressLevels;
}

export class FiasApi {
    private readonly baseUrl: string;
    private readonly maxItems: number;
    private readonly maxSteadsCount: number;
    private readonly division: divisionType;
    private readonly addressLevel: addressLevels;
    private readonly searchSteads: boolean;

    public constructor(
        formUrl: string,
        maxItemsCount: number,
        division: divisionType,
        addressLevel: addressLevels,
        searchSteads: boolean = false,
        maxSteadsCount: number = maxSteads
    ) {
        this.baseUrl = this.setApiUrl(formUrl);
        this.maxItems = maxItemsCount;
        this.maxSteadsCount = maxSteadsCount;
        this.division = division;
        this.addressLevel = addressLevel;
        this.searchSteads = searchSteads;
    }

    private setApiUrl(url: string): string {
        if (url.includes("testkontur") || url.includes("localhost")) {
            return "https://api.testkontur.ru/fias/v1.2";
        } else {
            return "https://api.kontur.ru/fias/v1.2";
        }
    }

    public getApiUrl(): string {
        return this.baseUrl;
    }

    public async fetchSteadsByTextAndParentFiasId(text: string, parentFiasId: string): Promise<FiasStead[]> {
        const steadNumber = this.extractSteadNumberFromText(text);
        const URLRequest = this.createSteadUrlBySteadNumber(steadNumber, parentFiasId);
        const response = await fetch(URLRequest);
        const steads: FiasStead[] = await response.json();
        return steads;
    }

    public async fetchSuggestAddressesByText(text: string): Promise<FiasAddressFields[]> {
        const URLRequest = this.createAddressUrlByStringAddress(text);

        const response = await fetch(URLRequest);
        const suggestAddresses: FiasAddressFields[] = await response.json();

        const uniqAddresses = this.extractUniqAddresses(suggestAddresses);
        return uniqAddresses;
    }

    public async fetchFullAddressesByFiasId(fiasId: string): Promise<FiasAddressFields> {
        const URLRequest = this.createFullAddressUrlByFiasId(fiasId);

        const response = await fetch(URLRequest);
        if (response.status !== 200) {
            return {};
        }

        const address: FiasAddressFields = await response.json();
        if ("error" in address) {
            return {};
        }

        return address;
    }

    public async fetchFlatByParentFiasId(parentFiasId: string, number: string): Promise<FiasFlatRoom> {
        const URLRequest = this.createFullAddressUrlToFetchFlat(parentFiasId, number);

        const response = await fetch(URLRequest);
        const flat: FiasFlatRoom = await response.json();

        return flat;
    }

    public async fetchCountriesByName(countryName: string): Promise<FiasCountry[]> {
        const URLRequest = this.createFullAddressUrlToFetchCountry(countryName);

        const response = await fetch(URLRequest);
        const countries: FiasCountry[] = await response.json();

        return countries;
    }

    public async fetchRegions(): Promise<RegionsType> {
        const URLRequest = this.createRegionsRequestUrl();

        const response = await fetch(URLRequest);
        const regions: RegionsType = await response.json();
        return regions;
    }

    public convertAbbreviation(value: string, helper?: fiasConvertationMethods) {
        return acceptHelperConvertationToValue(value, helper);
    }

    private extractSteadNumberFromText(text: string): string {
        const spitedAddress = text?.split(" ");
        const steadNumber = spitedAddress && spitedAddress[spitedAddress.length - 1];
        const prevSymbol = spitedAddress && Number(spitedAddress[spitedAddress.length - 2]);
        const isPrevSymbolNumber = !prevSymbol && isNaN(prevSymbol || NaN);
        if (steadNumber && steadNumber.trim() && isPrevSymbolNumber) {
            return steadNumber;
        }
        return "";
    }

    private createAddressUrlByStringAddress(address: string): string {
        const preparedAddress = this.prepareAddressBeforeRequest(address);
        return (
            this.baseUrl +
            `/addresses/search?address=${preparedAddress}&limit=${this.maxItems}&level=${this.addressLevel}&division=${this.division}&schema=gar&searchSteads=${this.searchSteads}`
        );
    }

    private createSteadUrlBySteadNumber(steadNumber: string, parentFiasId: string): string {
        return (
            this.baseUrl +
            `/steads?prefix=${steadNumber}&parentFiasId=${parentFiasId}&limit=${this.maxSteadsCount}&division=${this.division}`
        );
    }

    private createRegionsRequestUrl(): string {
        return this.baseUrl + `/addresses/regions`;
    }

    private createFullAddressUrlByFiasId(fiasId: string): string {
        return this.baseUrl + `/addresses/structural/${fiasId}?division=${this.division}&schema=gar`;
    }

    private createFullAddressUrlToFetchFlat(houseId: string, flatNum: string): string {
        return this.baseUrl + `/rooms?prefix=${flatNum}&parentFiasId=${houseId}&limit=1&schema=gar`;
    }

    private createFullAddressUrlToFetchCountry(country: string): string {
        return this.baseUrl + `/countries?prefix=${country}&limit=${this.maxItems}`;
    }

    private extractUniqAddresses(addresses: FiasAddressFields[]): FiasAddressFields[] {
        return addresses.filter(
            (value, index, self) =>
                self.findIndex(address => address.suggest?.fiasId === value.suggest?.fiasId) === index
        );
    }

    private prepareAddressBeforeRequest(address: string | undefined): string {
        if (isNullOrEmpty(address)) {
            return "";
        }
        if (detectIE()) {
            return encodeURI(address);
        }
        return address.trim();
    }
}
